import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Shared EvalentX brand logo
 * Geometric "X" mark + EvalentX wordmark
 *
 * Props:
 *   subtitle?: string      -> e.g. "Arena", "for Companies"
 *   size?: 'sm'|'md'|'lg'  -> default 'md'
 *   inverted?: boolean     -> for dark backgrounds (e.g. footer)
 *   to?: string            -> link target, defaults to "/"
 */
const Logo = ({ subtitle, size = 'md', inverted = false, to = '/', noLink = false }) => {
  const SIZES = {
    sm: { mark: 'w-7 h-7', markText: 'text-sm', word: 'text-base', sub: 'text-[10px]' },
    md: { mark: 'w-9 h-9', markText: 'text-lg', word: 'text-xl', sub: 'text-[11px]' },
    lg: { mark: 'w-12 h-12', markText: 'text-2xl', word: 'text-3xl', sub: 'text-sm' },
  };
  const s = SIZES[size] || SIZES.md;

  const wordPrimary = inverted ? '#FFFFFF' : '#0A0A0A';
  const subColor = inverted ? '#A3A3A3' : '#525252';
  const dividerColor = inverted ? '#3F3F46' : '#E5E5E5';

  const inner = (
    <>
      {/* Mark */}
      <div
        className={`${s.mark} grid place-items-center rounded-[3px] border-[1.5px] border-[#B91C1C] bg-transparent transition-colors duration-300 group-hover:bg-[#FEF2F2]`}
        style={inverted ? { background: 'transparent' } : undefined}
      >
        <span
          className={`${s.markText} font-extrabold tracking-tighter text-[#B91C1C] leading-none`}
          style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
        >
          X
        </span>
      </div>

      {/* Wordmark */}
      <div className="flex items-center gap-3">
        <span
          className={`${s.word} font-extrabold tracking-tighter leading-none`}
          style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif", color: wordPrimary }}
        >
          Evalent<span className="text-[#B91C1C]">X</span>
        </span>

        {subtitle && (
          <>
            <span
              className="block w-px h-5"
              style={{ background: dividerColor }}
              aria-hidden="true"
            />
            <span
              className={`${s.sub} font-medium tracking-[0.05em] lowercase`}
              style={{ color: subColor }}
            >
              {subtitle}
            </span>
          </>
        )}
      </div>
    </>
  );

  if (noLink) {
    return (
      <span className="group inline-flex items-center gap-3 select-none" data-testid="brand-logo">
        {inner}
      </span>
    );
  }

  return (
    <Link
      to={to}
      className="group inline-flex items-center gap-3 select-none"
      data-testid="brand-logo"
    >
      {inner}
    </Link>
  );
};

export default Logo;
